/**
 * @voltxampere/engine — B-source expression-driven source stamp
 * (Sprint 54.2b-5 ADR-0087)
 *
 * SPICE B-element analog: voltage source (or current source) whose
 * value is the result of an expression evaluated per Newton iteration
 * against current iterate's V(net) / I(component) values.
 *
 * Canonical engine type `b-source`; params (Sprint 54.2b-3
 * BSourceParamsSchema):
 *   - expression: string — DSL source (see `../../dsl/`)
 *   - expressionType: 'voltage' | 'current'
 *
 * Voltage-source mode: augmented row pattern (Sprint 4 voltage-source
 * lineage). Output is V_target = evaluate(expr) volts between pos
 * and neg terminals.
 *
 * Current-source mode: stamps Norton current = evaluate(expr) amps.
 *
 * AST caching:
 *   The expression is parsed once per component instance into an
 *   AST and cached in a module-level Map (componentId → Expr).
 *   Subsequent Newton iters evaluate the cached AST. The cache is
 *   keyed by componentId+expressionString — if the user mutates the
 *   expression mid-solve the cache miss reparses. Stale entries
 *   accumulate until the engine restarts (acceptable for MVP;
 *   Sprint 55+ may add explicit cache invalidation hooks).
 *
 * Discontinuous if/else handling: B-source expressions can have
 * sharp branches (e.g. `(V(in) > 0.5) ? 5 : 0`). Newton convergence
 * around the branch depends on the existing engine's two-tier
 * convergence (Sprint 8b ADR-0019) — if the iterate flip-flops
 * across the threshold, the relaxed tier accepts a higher residual.
 * No special hysteresis here; user can add hysteresis via the
 * expression itself.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { defineStamp } from "../definition.js";
import { getTerminalNetId } from "../terminal-utils.js";
import { evaluate, parseSource } from "../../dsl/index.js";
const astCache = new Map();
function getAst(component) {
    const params = component.params;
    const expression = params.expression;
    if (typeof expression !== "string" || expression.length === 0) {
        throw new ContractViolationError(`bSourceStamp: component "${component.id}" missing 'expression' param`);
    }
    const cached = astCache.get(component.id);
    if (cached?.expression === expression) {
        return cached.ast;
    }
    let ast;
    try {
        ast = parseSource(expression);
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        throw new ContractViolationError(`bSourceStamp: component "${component.id}" expression parse failed: ${msg}`);
    }
    astCache.set(component.id, { expression, ast });
    return ast;
}
function getExpressionType(component) {
    const params = component.params;
    const t = params.expressionType;
    if (t === "current")
        return "current";
    if (t === "voltage")
        return "voltage";
    // Default to voltage for backward-compat with users who omit the
    // field; matches ngspice B-element default ("V").
    return "voltage";
}
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
/**
 * Build a runtime context for the DSL evaluator. The stamp captures
 * the engine context closures so V(net) / I(component) look up
 * against the current Newton iterate.
 */
function makeEvalContext(ctx, netNameToId, time) {
    return {
        nodeVoltage(net) {
            // The DSL uses net NAMES; the engine indexes by net ID. The
            // caller supplies a name→id map (built once at solve setup
            // from circuit.nets). If absent, fall back to treating the
            // DSL string as the id directly.
            const id = netNameToId?.get(net) ?? net;
            const idx = ctx.nodeIndex(id);
            return nodeVoltage(ctx.xPrev, idx);
        },
        componentCurrent(componentId) {
            if (!ctx.vSourceIndex)
                return 0;
            try {
                const idx = ctx.vSourceIndex(componentId);
                return nodeVoltage(ctx.xPrev, idx);
            }
            catch {
                // Component not augmented (not a V-source / inductor / etc.) →
                // current not directly readable. Return 0 — caller can use
                // V() then divide by R if they need a resistor current.
                return 0;
            }
        },
        time,
    };
}
/**
 * DC stamp — evaluate expression with time = 0; pulse() returns its
 * v1 (DC steady-state value, matching ngspice DC analysis convention).
 */
export const bSourceDCStamp = (ctx, component) => {
    const ast = getAst(component);
    // MVP voltage-only mode (see definition note). expressionType:
    // "current" falls through to voltage stamp.
    void getExpressionType(component);
    const evalCtx = makeEvalContext(ctx, undefined, 0);
    const value = evaluate(ast, evalCtx);
    const pos = ctx.nodeIndex(getTerminalNetId(component, "pos"));
    const neg = ctx.nodeIndex(getTerminalNetId(component, "neg"));
    const k = ctx.vSourceIndex(component.id);
    ctx.addY(pos, k, 1);
    ctx.addY(neg, k, -1);
    ctx.addY(k, pos, 1);
    ctx.addY(k, neg, -1);
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + value * ctx.sourceScale;
};
/**
 * Transient stamp — same as DC but evaluator gets ctx.time so
 * pulse() returns the time-varying value.
 */
export const bSourceTransientStamp = (ctx, component) => {
    const ast = getAst(component);
    void getExpressionType(component);
    // Transient context exposes simulation time via ctx.t (Sprint 6
    // transient stamp context). When transient time is unavailable
    // (DC fallback), default to 0.
    const time = ctx.t ?? 0;
    const evalCtx = makeEvalContext(ctx, undefined, time);
    const value = evaluate(ast, evalCtx);
    const pos = ctx.nodeIndex(getTerminalNetId(component, "pos"));
    const neg = ctx.nodeIndex(getTerminalNetId(component, "neg"));
    const k = ctx.vSourceIndex(component.id);
    ctx.addY(pos, k, 1);
    ctx.addY(neg, k, -1);
    ctx.addY(k, pos, 1);
    ctx.addY(k, neg, -1);
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + value;
};
/**
 * Sprint 54.2b-5 MVP — B-source always allocates a vsource
 * augmented row. ngspice's B-element supports both voltage (V=...)
 * and current (I=...) modes, but the engine planner's vsource
 * allocator (`circuit-plan.ts`) only consults static augments —
 * dynamic per-instance vsource toggling would require planner
 * extension. For MVP we declare static 'vsource' and treat
 * current-mode B-sources as a Sprint 55+ extension: the
 * `expressionType: "current"` param falls through to voltage mode
 * with a warning emitted by the schema-side validator (Sprint
 * 54.3 fixture builder can flag this).
 */
// Sprint 54.3h engine ext #10: b-source.pos is volatile when the
// expression contains a comparator-style ternary (e.g. fathers
// circuit PWM: `(V(VDC) > V(VREF)) ? 5 : 0`). The DSL has no opt-in
// metadata for "this expression is volatile" — for the MVP we mark
// the pos terminal volatile UNCONDITIONALLY so the LTE controller
// excludes b-source outputs from the acceptance check. Future
// sprints can add expression-introspection that detects ternaries /
// comparisons and toggles volatility per-instance.
export const bSourceDCStampDefinition = defineStamp({
    type: "b-source",
    augments: ["vsource"],
    applyDC: bSourceDCStamp,
    applyTransient: bSourceTransientStamp,
    volatileTerminals: ["pos"],
});
export const bSourceTransientStampDefinition = bSourceDCStampDefinition;
/** Test helper — clear the per-instance AST cache. */
export function __resetBSourceCacheForTests() {
    astCache.clear();
}
//# sourceMappingURL=b-source.js.map